import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getClasses, createClass, updateClass, deleteClass, getClassBookings, checkInMember } from './coachApi';

const CLASS_TYPES = ['Yoga', 'Pilates', 'HIIT', 'Strength', 'Cardio', 'CrossFit', 'Spin', 'Boxing', 'Stretch', 'Custom'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const TYPE_COLORS = {
  Yoga: '#7B6FD0', Pilates: '#C8A84B', HIIT: '#C05050', Strength: '#D4873A',
  Cardio: '#5B9ED0', CrossFit: '#4AC87A', Spin: '#2A9E82', Boxing: '#C05050',
  Stretch: '#5B9ED0', Custom: _T?.accent || '#5B9ED0',
};

const emptyForm = { name: '', type: 'HIIT', day: 0, time: '09:00', duration: 60, capacity: 20, instructor: '', location: '', notes: '', recurring: true };

export default function CoachGymTab({ coachId, clients, lang, tier }) {
  const nl = lang === 'nl';
  const [classes, setClasses]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeClass, setActiveClass] = useState(null);
  const [bookings, setBookings]     = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);
  const [viewDay, setViewDay]       = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);

  const load = useCallback(() => {
    getClasses(coachId).then(d => { if (Array.isArray(d)) setClasses(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const loadBookings = useCallback((classId) => {
    getClassBookings(coachId, classId).then(d => { if (Array.isArray(d)) setBookings(d); }).catch(() => {});
  }, [coachId]);

  useEffect(() => { if (activeClass) loadBookings(activeClass.id); }, [activeClass, loadBookings]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await createClass(coachId, { ...form }).catch(() => {});
    setSaving(false);
    setShowForm(false);
    setForm(emptyForm);
    load();
  };

  const handleDelete = async (cid) => {
    if (!window.confirm(nl ? 'Les verwijderen?' : 'Delete class?')) return;
    await deleteClass(coachId, cid).catch(() => {});
    if (activeClass?.id === cid) setActiveClass(null);
    load();
  };

  const handleCheckIn = async (bookingId, userId) => {
    await checkInMember(coachId, activeClass.id, bookingId).catch(() => {});
    setBookings(bs => bs.map(b => b.id === bookingId ? { ...b, checked_in: true } : b));
  };

  const dayClasses = classes.filter(c => c.day === viewDay);

  // ── Class detail ────────────────────────────────────────────────────────────
  if (activeClass) {
    const checkedIn = bookings.filter(b => b.checked_in).length;
    const total = bookings.length;
    const pct = activeClass.capacity > 0 ? Math.round((total / activeClass.capacity) * 100) : 0;

    return (
      <div style={{ padding: '0 0 32px' }}>
        <button onClick={() => setActiveClass(null)} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 16px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `${TYPE_COLORS[activeClass.type] || _T.accent}20`, border: `2px solid ${TYPE_COLORS[activeClass.type] || _T.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: TYPE_COLORS[activeClass.type] || _T.accent }}>
            {activeClass.type?.slice(0, 2)}
          </div>
          <div>
            <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{activeClass.name}</div>
            <div style={{ color: C.silver, fontSize: 12 }}>{DAYS[activeClass.day]} · {activeClass.time} · {activeClass.duration}min</div>
            {activeClass.instructor && <div style={{ color: C.silver2, fontSize: 11, marginTop: 2 }}>👤 {activeClass.instructor}</div>}
          </div>
        </div>

        {/* Capacity bar */}
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700 }}>{nl ? 'BEZETTING' : 'OCCUPANCY'}</div>
            <div style={{ fontSize: 13, color: C.white, fontWeight: 700 }}>{total}/{activeClass.capacity} · {pct}%</div>
          </div>
          <div style={{ background: _T.bg4, borderRadius: 6, height: 10, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 6, background: pct >= 90 ? '#C05050' : pct >= 70 ? '#C8A84B' : '#4AC87A', transition: 'width 0.4s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#4AC87A', fontSize: 18, fontWeight: 700 }}>{checkedIn}</div>
              <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Ingecheckt' : 'Checked In'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{total - checkedIn}</div>
              <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Verwacht' : 'Expected'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: C.silver, fontSize: 18, fontWeight: 700 }}>{activeClass.capacity - total}</div>
              <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Open plekken' : 'Open Spots'}</div>
            </div>
          </div>
        </div>

        {/* Bookings list */}
        <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 10 }}>{nl ? 'DEELNEMERS' : 'PARTICIPANTS'}</div>
        {bookings.length === 0 ? (
          <div style={{ color: C.silver2, fontSize: 13 }}>{nl ? 'Nog geen boekingen' : 'No bookings yet'}</div>
        ) : (
          bookings.map(b => {
            const client = (clients || []).find(c => c.user_id === b.user_id);
            return (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: _T.bg3, border: `1px solid ${b.checked_in ? '#4AC87A33' : _T.cardBorderBot}`, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: b.checked_in ? '#4AC87A20' : `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                  {b.checked_in ? '✓' : (client?.avatar || '👤')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{client?.name || b.name || `User ${b.user_id}`}</div>
                  <div style={{ color: b.checked_in ? '#4AC87A' : C.silver2, fontSize: 11 }}>{b.checked_in ? (nl ? 'Ingecheckt' : 'Checked in') : (nl ? 'Aangemeld' : 'Booked')}</div>
                </div>
                {!b.checked_in && (
                  <button onClick={() => handleCheckIn(b.id, b.user_id)}
                    style={{ background: '#4AC87A20', border: '1px solid #4AC87A44', borderRadius: 8, color: '#4AC87A', fontSize: 11, fontWeight: 700, padding: '5px 10px', cursor: 'pointer' }}>
                    {nl ? 'Check-in' : 'Check In'}
                  </button>
                )}
              </div>
            );
          })
        )}

        {/* Add from clients not booked */}
        {(clients || []).filter(c => !bookings.some(b => b.user_id === c.user_id)).length > 0 && (
          <>
            <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginTop: 16, marginBottom: 10 }}>{nl ? 'HANDMATIG TOEVOEGEN' : 'MANUAL ADD'}</div>
            {(clients || []).filter(c => !bookings.some(b => b.user_id === c.user_id)).slice(0, 5).map(c => (
              <div key={c.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${C.silver}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, opacity: 0.6 }}>{c.avatar || '👤'}</div>
                <div style={{ flex: 1, color: C.silver, fontSize: 13 }}>{c.name}</div>
                <button onClick={async () => {
                  if (total >= activeClass.capacity) return;
                  // optimistic add
                  const mock = { id: `mock_${c.user_id}`, user_id: c.user_id, checked_in: false, name: c.name };
                  setBookings(bs => [...bs, mock]);
                }} style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 12, fontWeight: 700, padding: '5px 12px', cursor: total >= activeClass.capacity ? 'not-allowed' : 'pointer', opacity: total >= activeClass.capacity ? 0.4 : 1 }}>
                  +
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Gym & Lessen' : 'Gym & Classes'}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{classes.length} {nl ? 'lessen gepland' : 'classes scheduled'}</div>
        </div>
        <button onClick={() => setShowForm(v => !v)}
          style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
          + {nl ? 'Les' : 'Class'}
        </button>
      </div>

      {/* New class form */}
      {showForm && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'NIEUWE LES' : 'NEW CLASS'}</div>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={nl ? 'Lesnaam *' : 'Class name *'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }}>
              {CLASS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.day} onChange={e => setForm(f => ({ ...f, day: +e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }}>
              {DAYS.map((d, i) => <option key={d} value={i}>{nl ? DAYS_NL[i] : d}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
            <input type="number" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: +e.target.value }))}
              placeholder={nl ? 'Duur (min)' : 'Duration (min)'}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
            <input type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: +e.target.value }))}
              placeholder={nl ? 'Capaciteit' : 'Capacity'}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input value={form.instructor} onChange={e => setForm(f => ({ ...f, instructor: e.target.value }))}
              placeholder={nl ? 'Instructeur' : 'Instructor'}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder={nl ? 'Locatie' : 'Location'}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.recurring} onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))} />
            <span style={{ color: C.silver, fontSize: 13 }}>{nl ? 'Wekelijks terugkerend' : 'Weekly recurring'}</span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowForm(false)} style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {nl ? 'Annuleren' : 'Cancel'}
            </button>
            <button onClick={handleCreate} disabled={saving || !form.name.trim()}
              style={{ flex: 1, background: form.name.trim() ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: form.name.trim() ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 13, padding: '10px', cursor: form.name.trim() ? 'pointer' : 'not-allowed' }}>
              {saving ? '...' : (nl ? 'Aanmaken' : 'Create')}
            </button>
          </div>
        </div>
      )}

      {/* Day filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {DAYS.map((d, i) => {
          const cnt = classes.filter(c => c.day === i).length;
          return (
            <button key={d} onClick={() => setViewDay(i)}
              style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 10, border: `1.5px solid ${viewDay === i ? _T.accent : _T.cardBorderBot}`, background: viewDay === i ? `${_T.accent}20` : _T.bg4, color: viewDay === i ? _T.accent : C.silver, fontSize: 12, fontWeight: viewDay === i ? 700 : 400, cursor: 'pointer' }}>
              {nl ? DAYS_NL[i] : d}{cnt > 0 ? ` (${cnt})` : ''}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div>
      ) : dayClasses.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏋️</div>
          <div style={{ fontSize: 14 }}>{nl ? 'Geen lessen op deze dag' : 'No classes on this day'}</div>
        </div>
      ) : (
        dayClasses.sort((a, b) => a.time?.localeCompare(b.time)).map(cls => {
          const typeColor = TYPE_COLORS[cls.type] || _T.accent;
          const booked = cls.booked_count || 0;
          const pct = cls.capacity > 0 ? Math.round((booked / cls.capacity) * 100) : 0;
          return (
            <div key={cls.id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: `${typeColor}20`, border: `2px solid ${typeColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: typeColor }}>
                  {cls.type?.slice(0, 3)?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{cls.name}</div>
                  <div style={{ color: C.silver, fontSize: 12 }}>{cls.time} · {cls.duration}min{cls.instructor ? ` · ${cls.instructor}` : ''}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <div style={{ flex: 1, background: _T.bg4, borderRadius: 4, height: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct >= 90 ? '#C05050' : pct >= 70 ? '#C8A84B' : '#4AC87A', borderRadius: 4 }} />
                    </div>
                    <div style={{ color: C.silver2, fontSize: 10 }}>{booked}/{cls.capacity}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setActiveClass(cls)}
                    style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>
                    {nl ? 'Beheer' : 'Manage'}
                  </button>
                  <button onClick={() => handleDelete(cls.id)}
                    style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 18, cursor: 'pointer' }}>×</button>
                </div>
              </div>
              {cls.recurring && (
                <div style={{ marginTop: 8, fontSize: 10, color: C.silver2 }}>🔄 {nl ? 'Wekelijks terugkerend' : 'Weekly recurring'}</div>
              )}
            </div>
          );
        })
      )}

      {/* Week overview */}
      {!loading && classes.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 12 }}>{nl ? 'WEEKOVERZICHT' : 'WEEK OVERVIEW'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {DAYS.map((d, i) => {
              const cnt = classes.filter(c => c.day === i).length;
              return (
                <div key={d} onClick={() => setViewDay(i)} style={{ cursor: 'pointer', borderRadius: 10, padding: '8px 4px', textAlign: 'center', background: viewDay === i ? `${_T.accent}20` : cnt > 0 ? _T.bg3 : _T.bg4, border: `1px solid ${viewDay === i ? _T.accent + '55' : _T.cardBorderBot}` }}>
                  <div style={{ fontSize: 9, color: viewDay === i ? _T.accent : C.silver2, fontWeight: 700 }}>{nl ? DAYS_NL[i] : d}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: cnt > 0 ? (viewDay === i ? _T.accent : C.white) : C.silver2, marginTop: 2 }}>{cnt}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
